import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { filter, fromEvent, map, merge, tap } from 'rxjs';

import { pcdManager } from '@/manager';
import { useSubscriptions } from '@/package';
import {
  Close,
  EmergencyHome,
  VButton,
  VFlex,
  VIconButton,
  VInput,
  VSpacer,
  VStack,
} from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useLabelingBoxStore,
  usePcdRenderingStore,
  useToolMenuStore,
} from '@/store';
import type { BoxWithIndex } from '@/type';
import appWorker from '@/worker';

import InvalidCountIndicator from './InvalidCountIndicator';
import InvalidList from './InvalidList';

type ValidationResult = {
  emptyBoxList: BoxWithIndex[];
  invalidBoxList: BoxWithIndex[];
  overlappingGroupList: BoxWithIndex[][];
};

const ToolLabelingBoxValidationPopup = () => {
  const { t } = useTranslation();
  const {
    pcdIndexBoxListMap,
    getOverlappingGroupList,
    selectedBoxIdMap,
    selectBox,
    selectBoxList,
    removeBoxList,
  } = useLabelingBoxContext();
  const { pcdIndex, getPcdData } = usePcdRenderingStore();
  const { deselect, findByName, isSelected } = useToolMenuStore();
  const [validationResultByPcdIndex, setValidationResultByPcdIndex] = useState<
    Dictionary<ValidationResult>
  >({});
  const boxList = useMemo(
    () =>
      useLabelingBoxStore
        .getState()
        .getCurrentBoxList()
        .map((box, index) => ({ ...box, index }) as BoxWithIndex),
    [pcdIndex, pcdIndexBoxListMap],
  );
  const validationResult = validationResultByPcdIndex[pcdIndex] ?? {
    overlappingGroupList: [],
    invalidBoxList: [],
    emptyBoxList: [],
  };
  const count = useMemo(() => {
    const { emptyBoxList, invalidBoxList, overlappingGroupList } =
      validationResult;
    const empty = emptyBoxList?.length ?? 0;
    const invalid = invalidBoxList?.length ?? 0;
    const overlapping =
      overlappingGroupList?.reduce((sum, cur) => sum + cur.length, 0) ?? 0;
    const total = empty + invalid + overlapping;

    return {
      empty,
      invalid,
      overlapping,
      total,
    };
  }, [boxList, validationResult]);
  const boxListRef = useRef(boxList);

  const boxValidationTool = findByName('boxValidation')!;
  const visible = isSelected(boxValidationTool);

  const invalidBoxList = (countByBoxID: Dictionary<number>) => {
    const validationResult = Object.entries(countByBoxID).reduce(
      (acc, [boxID, count]) => {
        if (count > 4) return acc;

        acc[count > 0 ? 'invalidBoxList' : 'emptyBoxList'].push(
          boxList.find(box => box.id === boxID)!,
        );

        return acc;
      },
      {
        emptyBoxList: [] as BoxWithIndex[],
        invalidBoxList: [] as BoxWithIndex[],
      },
    );

    setValidationResultByPcdIndex(prev => ({
      ...prev,
      [pcdIndex]: {
        ...prev[pcdIndex],
        ...validationResult,
      },
    }));
  };

  const onLabelingBoxClick = (boxIDList: BoxWithIndex['id'][]) => {
    if (boxIDList.length === 1) {
      selectBox(boxIDList[0], true);
      return;
    }

    selectBoxList(boxIDList);
  };

  useSubscriptions(
    () => [
      merge(
        fromEvent(pcdManager, 'sortedGeometry'),
        fromEvent(pcdManager, 'afterChangeGeometry'),
      )
        .pipe(
          filter(() => visible),
          map(() => getPcdData(pcdIndex)?.pointList),
          filter(pcdPointList => pcdPointList != null),
          tap(pcdPointList => {
            appWorker
              .request('pcd', 'countPointPerBoxFromBlock', {
                boxList: boxListRef.current,
                pcdPointList,
              })
              .then(result => {
                invalidBoxList(result);
              });
          }),
        )
        .subscribe(),
    ],
    [visible],
  );

  useEffect(() => {
    if (!visible) return;

    setValidationResultByPcdIndex(prev => ({
      ...prev,
      [pcdIndex]: {
        ...prev[pcdIndex],
        overlappingGroupList: getOverlappingGroupList(),
      },
    }));
  }, [pcdIndex, visible, getOverlappingGroupList]);

  useEffect(() => {
    if (!visible) return;

    appWorker
      .request('pcd', 'countPointPerBoxFromBlock', {
        boxList,
        pcdPointList: getPcdData(pcdIndex)?.pointList ?? new Float32Array(),
      })
      .then(result => {
        invalidBoxList(result);
      });
  }, [boxList, visible]);

  useEffect(() => {
    boxListRef.current = boxList;
  }, [boxList]);

  return visible ? (
    <VStack
      className="absolute top-0 left-0 h-full w-[306px] bg-white p-2 pb-20"
      gap={1}
    >
      <VFlex justify="between" items="center">
        <div className="text-body-lg font-semibold text-blue-300">
          {t('labeling.boxValidationTitle.flattie')}
        </div>
        <div>
          <VIconButton
            Icon={Close}
            className="text-gray-400!"
            size="sm"
            variant="roundSecondary"
            onClick={() => deselect(boxValidationTool.id)}
          />
        </div>
      </VFlex>

      <VFlex className="hidden">
        <VButton fluid size="sm">
          {t('labeling.boxValidationValidateBoxesButtonText.fondlike')}
        </VButton>
      </VFlex>

      <VStack gap={0.25}>
        <VFlex justify="between" items="center">
          <div className="text-body-md font-semibold">
            {t('labeling.boxValidationResults.fortunetell')}
          </div>
          {count.total > 0 && (
            <div className="text-body-sm text-gray-400">
              Total {count.total}
            </div>
          )}
        </VFlex>

        <VFlex gap={0.25}>
          <VInput
            className="flex-1"
            label="Overlap"
            readOnly
            size="sm"
            orientation="horizontal"
            value={count.overlapping ? count.overlapping : '-'}
          />
        </VFlex>

        <VFlex gap={0.25}>
          <InvalidCountIndicator
            count={count.empty}
            label="Empty"
            onRemove={() =>
              removeBoxList(validationResult.emptyBoxList.map(b => b.id))
            }
          />

          <InvalidCountIndicator
            count={count.invalid}
            label="Invalid"
            tooltipMessage={t(
              'labeling.boxValidationInvalidInfoPopupMessage.glimmery',
            )}
            onRemove={() =>
              removeBoxList(validationResult.invalidBoxList.map(b => b.id))
            }
          />
        </VFlex>
      </VStack>

      <div className="bg-gray-300">
        <VSpacer size={0.25} />
      </div>

      <VStack className="flex-1 overflow-hidden" gap={0.25}>
        <div className="text-body-md font-semibold">
          {t('labeling.boxValidationList.glucolipine')}
        </div>

        {!(count.total - count.empty) ? (
          <div className="text-center text-body-md text-gray-400">
            {t('labeling.boxValidationEmptyList.gopura')}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <InvalidList
              boxGroupList={[validationResult?.emptyBoxList ?? []]}
              selectedBoxIdMap={selectedBoxIdMap}
              renderGroupTitle={() => (
                <>
                  <div className="text-error-500">
                    <EmergencyHome className="size-4" />
                  </div>
                  <div>Empty</div>
                </>
              )}
              onLabelingBoxClick={onLabelingBoxClick}
            />
            <InvalidList
              boxGroupList={[validationResult?.invalidBoxList ?? []]}
              selectedBoxIdMap={selectedBoxIdMap}
              renderGroupTitle={() => (
                <>
                  <div className="text-error-500">
                    <EmergencyHome className="size-4" />
                  </div>
                  <div>Invalid</div>
                </>
              )}
              onLabelingBoxClick={onLabelingBoxClick}
            />
            <InvalidList
              boxGroupList={validationResult?.overlappingGroupList ?? []}
              selectedBoxIdMap={selectedBoxIdMap}
              onLabelingBoxClick={onLabelingBoxClick}
            />
          </div>
        )}
      </VStack>
    </VStack>
  ) : (
    <></>
  );
};

export default ToolLabelingBoxValidationPopup;
